import {
  MATERIAL_ALPHA_PROPERTY,
  requestNativeGlassRefresh,
} from "@/desktop/native-glass-settings";
import { isTauri } from "@/lib/is-tauri";

export const TRANSPARENCY_STORAGE_KEY = "denker-transparency";
export const TRANSPARENCY_CHANGED_EVENT = "denker:transparency-changed";
export const TRANSPARENCY_TAURI_EVENT = "denker://transparency-changed";
/** 100 = today's glass look; 0 = every surface material painted solid. */
export const TRANSPARENCY_DEFAULT = 100;

const TRANSPARENCY_MIN = 0;
const TRANSPARENCY_MAX = 100;

let storageListenerInstalled = false;

/** An integer 0–100, or `null` for anything else. */
export function parseTransparency(value: unknown): number | null {
  return typeof value === "number" &&
    Number.isInteger(value) &&
    value >= TRANSPARENCY_MIN &&
    value <= TRANSPARENCY_MAX
    ? value
    : null;
}

function normalizeTransparency(value: number): number {
  if (!Number.isFinite(value)) return TRANSPARENCY_DEFAULT;
  return Math.round(
    Math.min(TRANSPARENCY_MAX, Math.max(TRANSPARENCY_MIN, value)),
  );
}

export function getStoredTransparency(): number {
  try {
    const stored = localStorage.getItem(TRANSPARENCY_STORAGE_KEY);
    if (stored === null || stored.trim() === "") return TRANSPARENCY_DEFAULT;
    return parseTransparency(Number(stored)) ?? TRANSPARENCY_DEFAULT;
  } catch {
    return TRANSPARENCY_DEFAULT;
  }
}

/** The solid fraction `A = (100 − v) / 100` every surface material lerps by. */
export function materialAlphaFor(value: number): number {
  return (TRANSPARENCY_MAX - normalizeTransparency(value)) / TRANSPARENCY_MAX;
}

export function applyTransparency(value: number): void {
  const style = document.documentElement.style;
  const next = String(materialAlphaFor(value));
  if (style.getPropertyValue(MATERIAL_ALPHA_PROPERTY) === next) return;
  style.setProperty(MATERIAL_ALPHA_PROPERTY, next);
}

export function persistTransparency(value: number): void {
  try {
    localStorage.setItem(TRANSPARENCY_STORAGE_KEY, String(value));
  } catch {
    // localStorage unavailable
  }
}

function dispatchTransparencyChanged(value: number): void {
  window.dispatchEvent(
    new CustomEvent(TRANSPARENCY_CHANGED_EVENT, { detail: { value } }),
  );
}

export function setTransparency(value: number): void {
  const normalized = normalizeTransparency(value);
  persistTransparency(normalized);
  applyTransparency(normalized);
  dispatchTransparencyChanged(normalized);
  if (isTauri()) {
    void Promise.all([
      import("@tauri-apps/api/event"),
      import("@tauri-apps/api/window"),
    ])
      .then(([{ emit }, { getCurrentWindow }]) =>
        emit(TRANSPARENCY_TAURI_EVENT, {
          value: normalized,
          source: getCurrentWindow().label,
        }),
      )
      .catch(() => {});
  }
  requestNativeGlassRefresh();
}

/**
 * Stamps the stored value and, once per document, follows changes made by
 * other browsing contexts of this origin (web tabs). Native-glass windows
 * add the Tauri event through `useTransparencySync()`.
 */
export function hydrateStoredTransparency(): void {
  applyTransparency(getStoredTransparency());
  if (storageListenerInstalled) return;
  storageListenerInstalled = true;
  window.addEventListener("storage", (event: StorageEvent) => {
    if (event.key !== TRANSPARENCY_STORAGE_KEY) return;
    const value = getStoredTransparency();
    applyTransparency(value);
    dispatchTransparencyChanged(value);
    requestNativeGlassRefresh();
  });
}
